import { useEffect, useState, useRef } from "react";
import styles from "../styles/groups.module.css";
import { getGroups, authedFetch } from "./helpers/requests.tsx";
import ConfirmDeleteGroupModal from "./modals/confirmDeleteGroupModal.tsx";
import ChangeGroupDataModal from "./modals/changeGroupDataModal.tsx";
import ConfirmHideModal from "./modals/confirmHideModal.tsx";
import ConfirmPermissionModal from "./modals/confirmPermissionModal.tsx";
import { useTranslation } from "react-i18next";

interface SelectedTraining {
  id?: string;
  name?: string;
  entityId?: string;
  permission?: boolean;
  scenarioId?: string;
  isHidden?: boolean;
}

const Groups = ({ groups, setGroups }) => {
  const [isOptionsOpen, setIsOptionsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const [trainingsList, setTrainingsList] = useState<any[]>([]);
  const [comingSoonScenarios, setComingSoonScenarios] = useState<any[]>([]);
  const [isLoadingGroups, setIsLoadingGroups] = useState(false);
  const [isLoadingTrainings, setIsLoadingTrainings] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [selectedGroupName, setSelectedGroupName] = useState("");
  const [selectedGroupCountryId, setSelectedGroupCountryId] = useState("");
  const [selectedGroupCountryCode, setSelectedGroupCountryCode] = useState("");
  const [selectedGroupHeadquarterId, setSelectedGroupHeadquarterId] =
    useState("");
  const [selectedGroupHeadquarterName, setSelectedGroupHeadquarterName] =
    useState("");
  const [showConfirmDeleteGroupModal, setShowConfirmDeleteGroupModal] =
    useState(false);
  const [editingGroups, setEditingGroups] = useState<{
    [key: string]: boolean;
  }>({});
  const [showChangeDataModal, setShowChangeDataModal] = useState(false);
  const [showConfirmPermissionModal, setShowConfirmPermissionModal] =
    useState(false);
  const [selectedTraining, setSelectedTraining] = useState<SelectedTraining>(
    {}
  );

  const [visibility, setVisibility] = useState({});
  const [showConfirmHideModal, setShowConfirmHideModal] = useState(false);
  const [selectedScenario, setSelectedScenario] = useState("");
  const [selectedScenarioName, setSelectedScenarioName] = useState("");

  const { t, i18n } = useTranslation();

  useEffect(() => {
    const fetchData = async () => {
      setIsLoadingGroups(true);

      const groupList = await getGroups();
      setGroups(groupList);

      setIsLoadingGroups(false);
    };

    fetchData();

    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOptionsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);

      setGroups([]);
      setSelectedGroupId("");
      setSelectedGroupName("");
      setSelectedGroupCountryId("");
      setSelectedGroupCountryCode("");
      setSelectedGroupHeadquarterId("");
      setSelectedGroupHeadquarterName("");
      setEditingGroups({});
      setTrainingsList([]);
    };
  }, [setGroups]);

  const updateGroupPermission = async (
    groupId: string,
    trainingId: string,
    isChecked: boolean,
    isHidden: boolean = false
  ) => {
    try {
      await authedFetch(`api/requests/updateTrainingPermission`, {
        method: "POST",
        body: JSON.stringify({
          entityId: groupId,
          entityType: "group",
          trainingId: trainingId,
          isChecked: isChecked,
          isHidden: isHidden,
        }),
      });
    } catch (error) {
      console.error("Error update group training permission:", error);
      throw new Error(
        "An error occurred while update group training permission"
      );
    }
  };

  const fetchGroupTrainingsList = async (groupId: string) => {
    setIsLoadingTrainings(true);
    try {
      const showAll = true;
      const trainings = await authedFetch(
        `api/requests/getTrainingsListWithPermissions`,
        {
          method: "POST",
          body: JSON.stringify({
            entityId: groupId,
            entityType: "group",
            showAll: showAll,
          }),
        }
      );

      setTrainingsList(trainings);

      // Initialize visibility state
      const initialVisibility = {};
      trainings.forEach((training) => {
        initialVisibility[training.scenarioId] =
          initialVisibility[training.scenarioId] || training.isHidden;
      });
      setVisibility(initialVisibility);
    } catch (error) {
      console.error("Error get group trainings list:", error);
      throw new Error("An error occurred while get group trainings list");
    } finally {
      setIsLoadingTrainings(false);
    }
  };

  const fetchComingSoonScenarios = async (groupId: string) => {
    setIsLoadingTrainings(true);

    try {
      const scenarios = await authedFetch(
        `api/requests/getComingSoonScenarios`,
        {
          method: "POST",
          body: JSON.stringify({
            entityId: groupId,
            entityType: "group",
            showAll: true,
          }),
        }
      );

      setComingSoonScenarios(scenarios);

      setVisibility((prevVisibility) => {
        const updatedVisibility = { ...prevVisibility };
        scenarios.forEach((scenario) => {
          updatedVisibility[scenario.id] = scenario.isHidden;
        });
        return updatedVisibility;
      });
    } catch (error) {
      console.error("Error get coming soon scenarios:", error);
      throw new Error("An error occurred while get coming soon scenarios");
    } finally {
      setIsLoadingTrainings(false);
    }
  };

  const toggleDropdown = () => {
    setIsOptionsOpen(!isOptionsOpen);
  };

  const renderChangeDataModal = () => {
    return (
      <ChangeGroupDataModal
        setShowChangeDataModal={setShowChangeDataModal}
        groupId={selectedGroupId}
        groupName={selectedGroupName}
        groupCountryId={selectedGroupCountryId}
        groupCountryCode={selectedGroupCountryCode}
        headquarterId={selectedGroupHeadquarterId}
        showSelectHQ={true}
        handleChangeData={handleChangeData}
      />
    );
  };

  const renderConfirmDeleteGroupModal = () => {
    return (
      <ConfirmDeleteGroupModal
        setShowConfirmDeleteGroupModal={setShowConfirmDeleteGroupModal}
        groupId={selectedGroupId}
        groupName={selectedGroupName}
        groupCountryCode={selectedGroupCountryCode}
        handleDeleteGroupSubmit={handleDeleteGroupSubmit}
      />
    );
  };

  const handleDeleteGroupSubmit = async () => {
    setGroups((prevGroups) =>
      prevGroups.filter((group) => group.id !== selectedGroupId)
    );
    setSelectedGroupId("");
    setSelectedGroupName("");
    setSelectedGroupCountryId("");
    setSelectedGroupCountryCode("");
    setSelectedGroupHeadquarterId("");
    setSelectedGroupHeadquarterName("");
    setEditingGroups({});
    setTrainingsList([]);
  };

  const handleChangeData = async (
    newGroupName: string,
    newGroupCountryId: string,
    newGroupCountryCode: string,
    newHeadquarterId: string,
    newHeadquarterName: string
  ) => {
    setGroups((prevGroups) =>
      prevGroups.map((group) => {
        if (group.id === selectedGroupId) {
          return {
            ...group,
            name: newGroupName,
            countryCode: newGroupCountryCode,
            headquarterName: newHeadquarterName,
          };
        }
        return group;
      })
    );

    handleEditClick(
      selectedGroupId,
      newGroupName,
      newGroupCountryId,
      newGroupCountryCode,
      newHeadquarterId,
      newHeadquarterName
    );

    setShowChangeDataModal(false);

    setSelectedGroupName(newGroupName);
    setSelectedGroupCountryId(newGroupCountryId);
    setSelectedGroupCountryCode(newGroupCountryCode);
    setSelectedGroupHeadquarterId(newHeadquarterId);
    setSelectedGroupHeadquarterName(newHeadquarterName);
  };

  const handleEditClick = async (
    groupId: string,
    groupName: string,
    groupCountryId: string,
    groupCountryCode: string,
    groupHeadquarterId: string,
    groupHeadquarterName: string
  ) => {
    setEditingGroups((prevState) => {
      const newState = { ...prevState };
      Object.keys(newState).forEach((key) => {
        newState[key] = false;
      });
      newState[groupId] = true;
      return newState;
    });

    setSelectedGroupId(groupId);
    setSelectedGroupName(groupName);
    setSelectedGroupCountryId(groupCountryId);
    setSelectedGroupCountryCode(groupCountryCode);
    setSelectedGroupHeadquarterId(groupHeadquarterId);
    setSelectedGroupHeadquarterName(groupHeadquarterName);

    await fetchGroupTrainingsList(groupId);
    await fetchComingSoonScenarios(groupId);
  };

  const handlePermissionChange = async (
    trainingId: string,
    targetId: string,
    permission: boolean
  ) => {
    // do in paralel as .net is too slow with Firestore requests
    updateGroupPermission(targetId, trainingId, permission);
    setTrainingsList(
      trainingsList.map((training) => {
        if (training.id === trainingId) {
          return { ...training, permission: permission };
        }

        return training;
      })
    );
    setSelectedTraining({});
  };

  const renderConfirmPermissionModal = () => {
    return (
      <ConfirmPermissionModal
        setShowConfirmPermissionModal={setShowConfirmPermissionModal}
        trainingId={selectedTraining.id}
        trainingName={selectedTraining.name}
        targetId={selectedTraining.entityId}
        targetName={selectedGroupName}
        permission={selectedTraining.permission}
        handlePermissionChange={handlePermissionChange}
      />
    );
  };

  const renderConfirmHideModal = () => {
    const isHidden = visibility[selectedScenario];
    if (isHidden) handleHideClick(selectedScenario);
    else {
      return (
        <ConfirmHideModal
          setShowConfirmHideModal={setShowConfirmHideModal}
          scenario={selectedScenario}
          scenarioName={selectedScenarioName}
          groupsView={true}
          handleHideClick={handleHideClick}
        />
      );
    }
  };

  const handleHideClick = (scenarioId) => {
    setShowConfirmHideModal(false);
    const isHidden = visibility[scenarioId];
    if (
      comingSoonScenarios.some(
        (comingSoonScenario) => comingSoonScenario.id === scenarioId
      )
    ) {
      // Update permissions to false for manufacturer
      const updatedComingSoonScenarios = comingSoonScenarios.map(
        (comingSoonScenario) => {
          if (comingSoonScenario.id === scenarioId) {
            updateGroupPermission(
              selectedGroupId,
              comingSoonScenario.id,
              true,
              !isHidden
            );
            return { ...comingSoonScenario, isHidden: !isHidden };
          }
          return comingSoonScenario;
        }
      );

      setComingSoonScenarios(updatedComingSoonScenarios);
    } else {
      // Update permissions to false for all trainings under this scenario
      const updatedTrainings = trainingsList.map((training) => {
        if (training.scenarioId === scenarioId) {
          updateGroupPermission(
            training.entityId,
            training.id,
            false,
            !isHidden
          );
          return { ...training, permission: false, isHidden: !isHidden };
        }
        return training;
      });

      setTrainingsList(updatedTrainings);
    }

    setVisibility((prevVisibility) => ({
      ...prevVisibility,
      [scenarioId]: !prevVisibility[scenarioId],
    }));
  };

  const groupTrainingsByScenario = (trainings) => {
    const groupedTrainings = {};
    trainings.forEach((training) => {
      const {
        scenarioId,
        scenarioName,
        name,
        id,
        permission,
        entityId,
        isHidden,
      } = training;
      if (!groupedTrainings[scenarioId]) {
        groupedTrainings[scenarioId] = {
          scenarioName: scenarioName,
          trainings: [],
        };
      }
      groupedTrainings[scenarioId].trainings.push({
        id: id,
        entityId: entityId,
        name: name,
        permission: permission,
        isHidden: isHidden,
      });
    });

    return groupedTrainings;
  };

  const renderGroupedTrainings = (groupedTrainings: {
    [scenarioId: string]: {
      scenarioName: string;
      trainings: {
        id: string;
        entityId: string;
        name: string;
        permission: boolean;
        isHidden: boolean;
      }[];
    };
  }) => {
    return (
      <>
        {Object.entries(groupedTrainings).map(
          ([scenarioId, { scenarioName, trainings }]) => {
            const isAnyTrainingHidden = trainings.some(
              (training) => training.isHidden
            );
            return (
              <div key={scenarioId} className={styles.trainingsGroup}>
                <label
                  className={styles.trainingsGroupTitle}
                  style={{ color: isAnyTrainingHidden ? "gray" : "#183a68" }}
                >
                  {scenarioName}
                </label>
                <span
                  onClick={() => {
                    setSelectedScenario(scenarioId);
                    setSelectedScenarioName(scenarioName);
                    setShowConfirmHideModal(true);
                  }}
                  style={{
                    marginLeft: "1vh",
                    fontSize: "0.7em",
                    cursor: "pointer",
                  }}
                >
                  {isAnyTrainingHidden ? t("show") : t("hide")}
                </span>
                <ul>
                  {trainings.map((training) => (
                    <li
                      key={training.id}
                      className="form-check custom-checkbox"
                      style={{ color: training.isHidden ? "gray" : "#212529" }}
                    >
                      <label className="custom-checkbox-label">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="agreeCheckbox"
                          checked={training.permission}
                          //disabled={training.isHidden}
                          onChange={(e) => {
                            setSelectedTraining((prevInfo) => ({
                              ...prevInfo,
                              id: training.id,
                              name: training.name,
                              entityId: training.entityId,
                              permission: training.permission,
                            }));
                            setShowConfirmPermissionModal(true);
                          }}
                        />
                        <span className="checkmark"></span>
                        <span className="form-check-label">
                          {training.name}
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
            );
          }
        )}
        {comingSoonScenarios.length > 0 && (
          <label
            className={styles.trainingsGroupTitle}
            style={{ color: "#183a68" }}
          >
            {t("Coming_soon")}:
          </label>
        )}
        {comingSoonScenarios.map(({ id, name, isHidden }) => {
          return (
            <div key={id} className={styles.trainingsGroup}>
              <label
                className={styles.trainingsGroupTitle}
                style={{ color: isHidden ? "gray" : "#212529" }}
              >
                {name}
              </label>
              <span
                onClick={() => {
                  setSelectedScenario(id);
                  setSelectedScenarioName(name);
                  setShowConfirmHideModal(true);
                }}
                style={{
                  marginLeft: "1vh",
                  fontSize: "0.7em",
                  cursor: "pointer",
                }}
              >
                {isHidden ? t("show") : t("hide")}
              </span>
            </div>
          );
        })}
      </>
    );
  };

  return (
    <div className={styles.adminContainer}>
      {isLoadingGroups ? (
        <p className={styles.loading}>{t("Loading...")}</p>
      ) : (
        <>
          {/* Left side */}
          <div className={styles.leftContainer}>
            {/* Separate table header - don't find other way to avoid overlaping header on scroll */}
            <div className={styles.tableHeader}>
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">{t("Group")}</th>
                    <th scope="col">{t("Country")}</th>
                    <th scope="col">{t("Headquarter")}</th>
                    <th scope="col" className={styles.usersColumn}>
                      {t("Users")}
                    </th>
                    <th scope="col"></th>
                    <th scope="col" className={styles.editButtonWrapper}></th>
                  </tr>
                </thead>
              </table>
            </div>

            {/* Table with restricted height and scrollbar */}
            <div className={styles.tableContent}>
              <div className={`container-fluid ${styles.containerFluid}`}>
                <table className="table">
                  <tbody>
                    {groups.map((group: any, index: number) => (
                      <tr
                        key={group.id}
                        style={{
                          backgroundColor:
                            index % 2 === 0 ? "#dbdfe7" : "transparent",
                        }}
                      >
                        <td>
                          {" "}
                          <img
                            src="/images/Icon_Hospital.png"
                            style={{
                              width: "2.5vh",
                              minWidth: "10px",
                              marginLeft: "0.3vh",
                              marginRight: "1vh",
                            }}
                          />
                          {group.name}
                        </td>
                        <td>{t("country_" + group.countryCode)}</td>
                        <td>{group.headquarterName}</td>
                        <td className={styles.usersColumn}>
                          {group.groupUsers}
                        </td>
                        <td>
                          {!group.hasGroupAdmin && (
                            <span style={{ color: "#a63939" }}>
                              {t("No_group_admin")}
                            </span>
                          )}
                        </td>
                        <td className={styles.editButtonWrapper}>
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() =>
                              handleEditClick(
                                group.id,
                                group.name,
                                group.country,
                                group.countryCode,
                                group.headquarterId,
                                group.headquarterName
                              )
                            }
                            style={{
                              backgroundColor: editingGroups[group.id]
                                ? "#404040"
                                : "#183a68",
                              borderColor: editingGroups[group.id]
                                ? "#404040"
                                : "#183a68",
                            }}
                          >
                            {t("Details")}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Right side */}
          <div className={styles.rightContainer}>
            {selectedGroupId ? (
              <>
                <div className={styles.containerHeaderWrapper}>
                  <div className={styles.containerHeader}>
                    <div className={styles.userDataWrapper}>
                      <div className={styles.userDataName}>
                        <img
                          src="/images/Icon_Hospital.png"
                          style={{
                            width: "5vh",
                            minWidth: "25px",
                            marginRight: "15px",
                          }}
                        />
                        {selectedGroupName}
                      </div>
                    </div>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                    }}
                  >
                    <div className="dropdown" ref={dropdownRef}>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={toggleDropdown}
                        style={{
                          width: "13vh",
                          minWidth: "60px",
                          backgroundColor: "#183a68",
                          borderColor: "#183a68",
                          borderRadius: "10px",
                          fontSize: "1em",
                          fontFamily: "VirtualLabQuickSandLight",
                          fontWeight: "bold",
                          paddingRight: "30px",
                        }}
                      >
                        <span
                          style={{
                            marginRight: "1.8vh",
                          }}
                        >
                          &#9013;
                        </span>
                        {t("Options")}
                      </button>
                      {isOptionsOpen && (
                        <div className={styles.dropdownMenu}>
                          <button
                            type="button"
                            onClick={() => {
                              setIsOptionsOpen(false);
                              setShowChangeDataModal(true);
                            }}
                            className="btn"
                          >
                            <img src="/images/Icon_Edit.png" />
                            {t("Edit_Group")}
                          </button>

                          <button
                            type="button"
                            onClick={() => {
                              setIsOptionsOpen(false);
                              setShowConfirmDeleteGroupModal(true);
                            }}
                            className="btn"
                          >
                            <img src="/images/Icon_Delete.png" />
                            {t("Delete_Group")}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  <hr />

                  <p className={styles.containerHeaderTitle}>
                    {t("Training_permission")}
                  </p>
                  <p className={styles.containerHeaderDescription}>
                    {t(
                      "Set_group_access_to_these_trainings_in_the_VR_application"
                    )}
                  </p>
                </div>

                <div className={`${styles.trainingsContainer} thin-scrollbar`}>
                  {isLoadingTrainings ? (
                    <p>{t("Loading...")}</p>
                  ) : (
                    renderGroupedTrainings(
                      groupTrainingsByScenario(trainingsList)
                    )
                  )}
                </div>
              </>
            ) : (
              <p className={styles.selectUser}>{t("Select_group_to_edit")}</p>
            )}
          </div>
        </>
      )}
      <div className={styles.adminContainer}>
        {(showConfirmDeleteGroupModal && renderConfirmDeleteGroupModal()) ||
          (showChangeDataModal && renderChangeDataModal()) ||
          (showConfirmHideModal && renderConfirmHideModal()) ||
          (showConfirmPermissionModal && renderConfirmPermissionModal())}
      </div>
    </div>
  );
};
export default Groups;
